import { BreakpointObserver } from '@angular/cdk/layout';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, inject, OnInit, PLATFORM_ID, Renderer2, signal, computed } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { Notification, NotificationResponse } from 'src/app/models/user-notification.model';
import { AppNotificationsService } from 'src/app/shared/services/app-notifications.service';
import { MusaffaRedirectService } from 'src/app/shared/services/musaffa-redirect.service';
import { SubscriptionService } from 'src/app/shared/services/subscription.service';
import { TimeAgoPipe } from 'src/app/shared/pipes/time-ago.pipe';
import { map } from 'rxjs';

@Component({
   selector: 'app-header-user-notifications',
   templateUrl: './header-user-notifications.component.html',
   styleUrls: ['./header-user-notifications.component.scss'],
   standalone: true,
   imports: [CommonModule, NgxSkeletonLoaderModule, TimeAgoPipe],
   changeDetection: ChangeDetectionStrategy.OnPush,
   host: {
      '(document:click)': 'onDocumentClick($event)'
   }
})
export class HeaderUserNotificationsComponent implements OnInit {
   private readonly renderer = inject(Renderer2);
   private readonly elementRef = inject(ElementRef);
   private readonly observer = inject(BreakpointObserver);
   private readonly notificationService = inject(AppNotificationsService);
   private readonly musaffaRedirectService = inject(MusaffaRedirectService);
   private readonly platformId = inject(PLATFORM_ID);

   private readonly isBrowser = isPlatformBrowser(this.platformId);

   private readonly subscriptionData = toSignal(SubscriptionService.subscriptionData);
   readonly userRole = computed(() => this.subscriptionData()?.role ?? null);
   
   readonly isMobile = toSignal(
      this.observer.observe('(max-width: 991px)').pipe(map(res => res.matches)),
      { initialValue: false }
   );

   readonly notificationsLoader = signal(false);
   readonly isNotificationsMenuOpen = signal(false);
   readonly isEveryNotificationRead = signal(false);
   readonly allNotifications = signal<Notification[]>([]);

   constructor() {
      // Manage notifications fetching side-effect
      SubscriptionService.subscriptionData
         .pipe(takeUntilDestroyed())
         .subscribe((data) => {
            if (!this.isBrowser) return;

            setTimeout(() => {
               if (data.role !== 'ANONYMOUS') {
                  this.getUserNotifications();
               }
            }, 0);
         });
   }

   ngOnInit(): void { }

   openNotificationsDropdown() {
      this.isNotificationsMenuOpen.set(true);
   }

   closeNotificationsMenu() {
      this.isNotificationsMenuOpen.set(false);
   }

   onDocumentClick(event: Event): void {
      if (!this.isBrowser) return;
      if (
         this.isNotificationsMenuOpen() &&
         !this.elementRef.nativeElement.contains(event.target)
      ) {
         this.closeNotificationsMenu();
      }
   }

   trackByNotificationId(index: number, notification: Notification) {
      return notification.id;
   }

   async getUserNotifications() {
      if (!this.isBrowser) return;

      try {
         this.notificationsLoader.set(true);
         const resp = await this.notificationService.fetchUserNotofications<NotificationResponse>();
         if (resp.status) {
            const filtered = resp.data.filter((n: Notification) => !!n.notification_log);
            this.allNotifications.set(filtered);

            const hasUnread = filtered.some((notification: Notification) => notification.is_read === false);
            this.isEveryNotificationRead.set(hasUnread);

            this.notificationService.updateNotificationRead(hasUnread);
         }
      } catch (error) {
         console.error(error);
      } finally {
         this.notificationsLoader.set(false);
      }
   }

   async onNotificationClick(notification: Notification) {
      try {
         const module = notification.notification_log?.module;
         const record_id = notification.notification_log?.record_id;

         if (!module || !record_id) {
            return;
         }

         this.musaffaRedirectService.goToPage(module, record_id);
         this.closeNotificationsMenu();
         await this.notificationService.markUserNotificationAsRead<{
            status: boolean;
            message: string;
         }>([notification.id]);
         this.getUserNotifications();
      } catch (error) {
         console.error(error);
      }
   }

   async markAllNotificationAsRead() {
      await this.notificationService.markAllNotificationAsRead();
      this.getUserNotifications();
      this.closeNotificationsMenu();
   }
}

